// src/components/StartVisualInspectionModal.js
// Shared "Start Visual Inspection" launcher — used from the SQF Hub (full batch/product picker),
// the Baking/Prep CCP Live pages (pre-filled from the live run's own batchId + its real product
// list — a Baking CCP run can schedule several products at once, so `productOptions` may hold more
// than one and the QA picks which one this particular spot-check is for), ProductionLog.js
// (pre-filled from the clicked row), and SqfChecklistListPage.js's generic "+ New Checklist" flow
// (via `presetTemplateId`/`presetTemplateName` — that page detects when the chosen template needs a
// batch/product and defers to this modal instead of creating a instance with neither, which used to
// be a dead end: the Fill page has no way to attach one after the fact). Creates an on-demand
// SqfChecklistInstance tied to the picked batch/product, then navigates straight to the Fill page —
// which starts the Log itself on load (POST /instances/:id/start), so no separate "start" call is
// needed here. The batch/product picker (shown whenever there's no pre-filled default) is backed by
// GET /visual-inspections/available-batches — NOT a plain "recent ProductionLog rows" list, since a
// batch only ever gets a ProductionLog row once a Baking/Prep CCP run is QA-verified. That left an
// actively-in-production run (or one that's finished production but hasn't been verified yet)
// completely unpickable here — exactly the gap the user hit. That endpoint unions real
// not-yet-verified CCP runs (both "still in production" and "done, awaiting QA") with recent
// ProductionLog rows for non-CCP manually-logged production.
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import sqfService from '../services/sqfService';
import SearchableSelect from './SearchableSelect';

const RECENT_WINDOW_DAYS = 14;

export default function StartVisualInspectionModal({ defaultBatchId, batchLabel, productOptions, presetTemplateId, presetTemplateName, onClose }) {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState(presetTemplateId ? String(presetTemplateId) : '');
  const [availableBatches, setAvailableBatches] = useState([]);
  const [batchProductKey, setBatchProductKey] = useState(''); // "batchId:productId"
  const [productId, setProductId] = useState(
    Array.isArray(productOptions) && productOptions.length === 1 ? String(productOptions[0].id) : ''
  );
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');

  const hasDefault = !!(defaultBatchId && Array.isArray(productOptions) && productOptions.length > 0);
  const needsProductPick = hasDefault && productOptions.length > 1;

  useEffect(() => {
    (async () => {
      try {
        if (!presetTemplateId) {
          const tmpls = await sqfService.getTemplates();
          // On-demand, published templates — the real filter for "is this a visual_inspection
          // template" would need fetching every version's schemaJson; on-demand + published is a
          // good-enough proxy for V1 since this feature is the main thing that trigger type is for.
          const eligible = tmpls.filter(tm => tm.triggerType === 'on_demand' && tm.activeVersionId);
          setTemplates(eligible);
          if (eligible.length === 1) setTemplateId(String(eligible[0].id));
        }

        if (!hasDefault) {
          const batches = await sqfService.getAvailableBatchesForInspection(RECENT_WINDOW_DAYS);
          setAvailableBatches(batches || []);
        }
      } catch {
        setError('Failed to load templates.');
      } finally {
        setLoading(false);
      }
    })();
  }, [hasDefault, presetTemplateId]);

  const handleStart = async () => {
    if (!templateId) { setError('Pick a template.'); return; }
    let batchId = defaultBatchId;
    let finalProductId = productId ? Number(productId) : null;
    if (!hasDefault) {
      if (!batchProductKey) { setError('Pick which batch/product you\'re inspecting.'); return; }
      const [b, p] = batchProductKey.split(':');
      batchId = Number(b);
      finalProductId = Number(p);
    } else if (!finalProductId) {
      setError('Pick which product you\'re inspecting.');
      return;
    }
    setStarting(true);
    setError('');
    try {
      const today = new Date().toISOString().split('T')[0];
      const instance = await sqfService.createInstance({ templateId: Number(templateId), dueDate: today, batchId, productId: finalProductId });
      navigate(`/sqf/fill/${instance.id}`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to start inspection.');
      setStarting(false);
    }
  };

  const batchProductOptions = availableBatches.map(b => ({
    value: `${b.batchId}:${b.productId}`,
    label: `${b.lotCode} — ${b.productName} (${b.statusLabel})`,
  }));

  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1060 }} onClick={onClose} />
      <div className="modal fade show d-block" style={{ zIndex: 1070 }} tabIndex={-1}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">📷 Start Visual Inspection</h5>
              <button className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}
              {loading ? (
                <div className="text-center py-3"><span className="spinner-border spinner-border-sm" /></div>
              ) : (
                <>
                  {hasDefault ? (
                    <div className="mb-3">
                      <div className="text-muted" style={{ fontSize: 12 }}>Inspecting</div>
                      <div style={{ fontWeight: 600 }}>{batchLabel}</div>
                      {needsProductPick ? (
                        <>
                          <label className="form-label small mb-1 mt-2">Which product? *</label>
                          <SearchableSelect
                            options={productOptions.map(p => ({ value: p.id, label: p.name }))}
                            value={productId}
                            onChange={setProductId}
                            placeholder="Select a product…"
                            size="sm"
                          />
                        </>
                      ) : (
                        <div style={{ fontSize: 13 }}>{productOptions[0]?.name}</div>
                      )}
                    </div>
                  ) : (
                    <div className="mb-3">
                      <label className="form-label small mb-1">Batch / Product *</label>
                      <SearchableSelect
                        options={batchProductOptions}
                        value={batchProductKey}
                        onChange={setBatchProductKey}
                        placeholder="Select a batch/product…"
                        size="sm"
                      />
                      {availableBatches.length === 0 && (
                        <div className="form-text">No production in progress, awaiting QA, or logged in the last {RECENT_WINDOW_DAYS} days.</div>
                      )}
                    </div>
                  )}

                  {presetTemplateId ? (
                    <div className="mb-2">
                      <div className="text-muted" style={{ fontSize: 12 }}>Checklist Template</div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{presetTemplateName}</div>
                    </div>
                  ) : (
                    <div className="mb-2">
                      <label className="form-label small mb-1">Checklist Template *</label>
                      <SearchableSelect
                        options={templates.map(tm => ({ value: tm.id, label: tm.name }))}
                        value={templateId}
                        onChange={setTemplateId}
                        placeholder="Select a template…"
                        size="sm"
                      />
                      {templates.length === 0 && (
                        <div className="form-text text-danger">
                          No published on-demand checklist template found. Build a "Visual Inspection" template in the SQF Hub first.
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary btn-sm" onClick={onClose} disabled={starting}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={handleStart} disabled={starting || loading || !templateId || (!hasDefault && !batchProductKey) || (needsProductPick && !productId)}>
                {starting ? 'Starting…' : 'Start Inspection'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
